import { useEffect, useRef, useState } from 'react';
import { PageHub } from '../components/PageHub';
import { seerrApi, type SeerrMedia, type SeerrRequest } from '../api';
import { linkProps, navigate } from '../router';
import { RequestCard, STATUS_LABEL } from '../components/RequestCard';

/** Verzoeken via Seerr: zoeken, aanvragen, en wat je al hebt aangevraagd. */
export function Requests({ initial }: { initial: string }) {
  const [status, setStatus] = useState<{ configured: boolean; canManage: boolean; canRequest: boolean } | null>(null);
  const [query, setQuery] = useState(initial);
  const [results, setResults] = useState<SeerrMedia[] | null>(null);
  const [trending, setTrending] = useState<SeerrMedia[]>([]);
  const [mine, setMine] = useState<{ requests: SeerrRequest[]; linked: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    seerrApi.status().then(setStatus).catch(() => setError('Seerr-status ophalen mislukt.'));
  }, []);

  const loadMine = () => seerrApi.requests().then(setMine).catch(() => setMine({ requests: [], linked: false }));

  useEffect(() => {
    if (!status?.configured) return;
    input.current?.focus();
    seerrApi.discover('trending').then((r) => setTrending(r.results)).catch(() => undefined);
    void loadMine();
  }, [status?.configured]);

  useEffect(() => {
    if (!status?.configured) return;
    const q = query.trim();
    navigate(q ? `/requests?q=${encodeURIComponent(q)}` : '/requests', { replace: true });
    if (q.length < 2) {
      setResults(null);
      return;
    }
    const t = window.setTimeout(() => {
      seerrApi.search(q).then((r) => { setResults(r.results); setError(null); }).catch(() => setError('Zoeken in Seerr mislukt.'));
    }, 350);
    return () => window.clearTimeout(t);
  }, [query, status?.configured]);

  if (status && !status.configured) {
    return (
      <main className="page">
        <header className="page-head"><h1>Verzoeken</h1></header>
        <div className="empty-card">
          <h2>Seerr is nog niet gekoppeld</h2>
          <p className="muted lead">
            {status.canManage
              ? 'Koppel je Seerr-server bij de instellingen. Daarna kun je hier films en series aanvragen.'
              : 'Vraag de eigenaar van de Plex-server om Seerr te koppelen.'}
          </p>
          {status.canManage && <a className="btn btn-primary" data-autofocus {...linkProps('/settings/connections')}>Naar instellingen</a>}
        </div>
      </main>
    );
  }

  const canRequest = status?.canRequest ?? false;

  return (
    <main className="page">
      <PageHub page="requests" />
      <header className="page-head"><h1>Verzoeken</h1></header>
      <form className="search-form" role="search" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="rq" className="visually-hidden">Zoek een film of serie</label>
        <input id="rq" ref={input} className="search-input" type="search" placeholder="Zoek een film of serie om aan te vragen…" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
      </form>
      {!canRequest && status && <p className="placeholder-row">Met dit profiel kun je kijken wat er is, maar geen verzoeken doen.</p>}
      {error && <p className="placeholder-row error" role="alert">{error}</p>}

      {results !== null ? (
        <section aria-label="Zoekresultaten">
          {results.length === 0 && <p className="placeholder-row">Niets gevonden voor “{query.trim()}”.</p>}
          <div className="req-grid" data-focus-group>
            {results.map((m) => <RequestCard key={`${m.mediaType}-${m.tmdbId}`} m={m} canRequest={canRequest} onRequested={loadMine} />)}
          </div>
        </section>
      ) : (
        <>
          {mine && mine.requests.length > 0 && (
            <section className="row" aria-label="Mijn verzoeken">
              <h2>Mijn verzoeken</h2>
              <div className="mine-list">
                {mine.requests.map((r) => r.media && (
                  <div key={r.id} className="mine-item">
                    <span className="mine-poster" style={r.media.poster ? { backgroundImage: `url("${r.media.poster}")` } : undefined} aria-hidden="true" />
                    <span className="mine-body">
                      <strong>{r.media.title}</strong>
                      <span className="card-sub">
                        {r.media.year}{r.seasons.length ? ` · seizoen ${r.seasons.join(', ')}` : ''} · {new Date(r.createdAt).toLocaleDateString('nl-NL')}
                      </span>
                    </span>
                    <span className={`req-status inline st-${r.approval === 'declined' ? 'declined' : r.approval === 'pending' ? 'pending' : r.media.status}`}>
                      {r.approval === 'declined' ? 'Afgewezen' : r.approval === 'pending' ? 'Wacht op goedkeuring' : STATUS_LABEL[r.media.status] || 'Goedgekeurd'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {mine && !mine.linked && (
            <p className="placeholder-row">Je Plex-account is nog niet bekend in Seerr; verzoeken komen op naam van de beheerder. Log één keer in bij Seerr met Plex om dit te koppelen.</p>
          )}
          {trending.length > 0 && (
            <section aria-label="Populair">
              <h2 className="section-title">Populair nu</h2>
              <div className="req-grid" data-focus-group>
                {trending.map((m) => <RequestCard key={`${m.mediaType}-${m.tmdbId}`} m={m} canRequest={canRequest} onRequested={loadMine} />)}
              </div>
            </section>
          )}
        </>
      )}
      <div className="footer-space" />
    </main>
  );
}
